import { GridRowSelectionModel, GridValidRowModel } from "@mui/x-data-grid"
import { Dispatch, SetStateAction, useState } from "react"
import { Permission } from "../../../../../interfaces/Security/permissions"
import { Box, Button, FormControlLabel, Switch, Typography } from "@mui/material"
import { ButtonLoading } from "../../../../../theme/buttons"
import { useTranslation } from "react-i18next"
import InfoIcon from '@mui/icons-material/Info';
import GenericPromises from "../../../../../api/GenericPromises"
import useSnackBar from "../../../../../components/Commons/SnackBar/useSnackBar"

interface BatchUpdatePermissionsProps {
  setOpenDialogUpdatePermissions: Dispatch<SetStateAction<boolean>>,
  permissionsIdsToUpdate: GridRowSelectionModel,
  permissionsData: readonly GridValidRowModel[],
  setIsLoadingAction: Dispatch<SetStateAction<boolean>>,
  isLoadingAction: boolean,
  handleUpdateRow: (updateRow: Permission) => void,
}

export const BatchUpdatePermissions = ({
  setOpenDialogUpdatePermissions,
  permissionsIdsToUpdate,
  permissionsData,
  setIsLoadingAction,
  isLoadingAction,
  handleUpdateRow,
}: BatchUpdatePermissionsProps) => {
  const [t] = useTranslation("global");
  const [create, setCreate] = useState(true);
  const [read, setRead] = useState(true);
  const [update, setUpdate] = useState(true);
  const [deletePermission, setDeletePermission] = useState(true);
  const { GenericPutResource } = GenericPromises();
  const { SnackbarComponent, showSnackBar } = useSnackBar();

  const onUpdatePermissions = async () => {
    setIsLoadingAction(true);

    try {
      const promises = permissionsIdsToUpdate.map(async (permissionId, id) => {
        const permission = permissionsData.find((permission) => permission.permissions_id === permissionId) as Permission;
        let myData = {
          group_id: permission.group_id,
          resource_id: permission.resource_id,
          create: create,
          read: read,
          update: update,
          delete: deletePermission,
        };

        return GenericPutResource(`/permissions/${permission.permissions_id}`, myData)
          .then((response) => {
            handleUpdateRow(response.data);
          })
          .catch((error) => {
            showSnackBar(error.message, 'error');
            throw error;
          });
      });

      await Promise.all(promises);

    } catch (error) {
      console.error("Error updating permissions:", error);
    } finally {
      setIsLoadingAction(false);
      setOpenDialogUpdatePermissions(false);
    }
  };

  return (
    <Box minWidth={1200}>
      <Box display={"flex"} alignItems={"center"}>
        <Box width={"100%"} marginBottom={4}>
          <Typography margin={1}><InfoIcon /> {t("permissions.info.update-permissions")}</Typography>
        </Box>
        <Box display="flex" gap={2}>
          <FormControlLabel
            control={<Switch checked={create} onChange={(e) => setCreate(e.target.checked)} />}
            label={t("permissions.fields.create")}
            disabled={isLoadingAction}
          />
          <FormControlLabel
            control={<Switch checked={read} onChange={(e) => setRead(e.target.checked)} />}
            label={t("permissions.fields.read")}
            disabled={isLoadingAction}
          />
          <FormControlLabel
            control={<Switch checked={update} onChange={(e) => setUpdate(e.target.checked)} />}
            label={t("permissions.fields.update")}
            disabled={isLoadingAction}
          />
          <FormControlLabel
            control={<Switch checked={deletePermission} onChange={(e) => setDeletePermission(e.target.checked)} />}
            label={t("permissions.fields.delete")}
            disabled={isLoadingAction}
          />
        </Box>
      </Box>
      <Box display={"flex"} flexDirection={"row-reverse"}>
        <ButtonLoading isLoading={isLoadingAction} onClick={() => onUpdatePermissions()}>
          {t("generic.buttons.update")}
        </ButtonLoading>
        <Button disabled={isLoadingAction} onClick={() => setOpenDialogUpdatePermissions(false)}>{t("generic.buttons.goback")}</Button>
      </Box>
      <SnackbarComponent />
    </Box>
  )
}
